import React, { useEffect } from "react";
import { Box, Typography, Stack, Chip, Button, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsFilePdf, BsWhatsapp } from "react-icons/bs";
import {
  TbBrandOpenai, TbMessageChatbot, TbEye, TbMicrophone, TbHandStop, TbPlugConnected, TbLayoutDashboard,
} from "react-icons/tb";
import { colors, font, pagePx } from "../theme/tokens";
import Reveal from "../components/Reveal";
import { LangGraphDiagram, WhatsAppFlowDiagram, ShopifySyncDiagram } from "../components/diagrams/Diagrams";

const Eyebrow = ({ children }) => (
  <Typography sx={{ fontFamily: font.mono, fontSize: 13, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase", color: colors.accent, mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box component="span" sx={{ width: 26, height: 2, background: colors.gradPrimary }} /> {children}
  </Typography>
);
const H2 = ({ children }) => (
  <Typography variant="h2" sx={{ fontFamily: font.sans, fontWeight: 700, fontSize: { xs: 24, md: 32 }, color: colors.ink, mb: 2, lineHeight: 1.2 }}>{children}</Typography>
);
const P = ({ children, sx }) => (
  <Typography sx={{ fontSize: { xs: 15, md: 16 }, color: colors.inkSoft, lineHeight: 1.8, mb: 2.5, ...sx }}>{children}</Typography>
);
const Strong = ({ children }) => <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}>{children}</Box>;
const Mono = ({ children, c = colors.amber }) => <Box component="span" sx={{ fontFamily: font.mono, color: c, fontSize: 14 }}>{children}</Box>;
const Section = ({ children, alt }) => (
  <Box component="section" sx={{ px: pagePx, py: { xs: 6, md: 9 }, backgroundColor: alt ? colors.bgAlt : "transparent" }}>
    <Box sx={{ maxWidth: 1080, mx: "auto" }}>{children}</Box>
  </Box>
);
const DiagramCard = ({ title, caption, children }) => (
  <Box sx={{ borderRadius: "18px", border: `1px solid ${colors.border}`, backgroundColor: colors.surface, p: { xs: 2, md: 3.5 }, my: 3 }}>
    {title && <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15, color: colors.ink, mb: 2.5 }}>{title}</Typography>}
    {children}
    {caption && <Typography sx={{ fontSize: 12.5, color: colors.inkMute, fontFamily: font.mono, mt: 2, textAlign: "center" }}>{caption}</Typography>}
  </Box>
);

const capabilities = [
  { icon: <TbMessageChatbot />, name: "Stateful agent", text: "A cyclic LangGraph state graph — the customer can change their mind, backtrack, or ask questions mid-order without breaking the flow." },
  { icon: <TbEye />, name: "Vision", text: "Customers can send a product photo over WhatsApp; the agent identifies and matches it against the catalogue." },
  { icon: <TbMicrophone />, name: "Voice (STT + TTS)", text: "Voice notes are transcribed; the agent can reply with a synthesized OpenAI voice — six selectable voices per store." },
  { icon: <TbHandStop />, name: "Human-in-the-loop", text: "Merchants can pause the AI on any conversation, reply manually, and resume — the agent keeps full context throughout." },
  { icon: <TbPlugConnected />, name: "Shopify sync", text: "OAuth connect with bi-directional product/order/customer sync and real-time webhooks." },
  { icon: <TbLayoutDashboard />, name: "Merchant dashboard", text: "Two React interfaces (merchant + admin) for catalogue, orders, conversations, agent config, and analytics." },
];

const stack = [
  { group: "Backend", items: "NestJS 11 (modular monolith, 15 modules)" },
  { group: "AI Engine", items: "LangGraph · GPT-4o-mini · function calling" },
  { group: "Messaging", items: "WhatsApp via Twilio (text · image · audio)" },
  { group: "Voice", items: "OpenAI TTS-1 · speech-to-text" },
  { group: "Integrations", items: "Shopify (OAuth 2.0 · webhooks)" },
  { group: "Data", items: "PostgreSQL 17 · TypeORM · JSONB" },
  { group: "Observability", items: "LangSmith tracing" },
  { group: "Frontend", items: "React (merchant + admin portals)" },
];

const shots = [
  { src: "/assets/orx_dashboard.jpg", label: "Merchant dashboard & analytics" },
  { src: "/assets/orx_agent.jpg", label: "AI agent configuration" },
  { src: "/assets/orx_conversations.jpg", label: "Live conversation monitoring" },
  { src: "/assets/orx_shopify.jpg", label: "Shopify integration" },
  { src: "/assets/orx_wa_order.jpg", label: "WhatsApp — order taking" },
  { src: "/assets/orx_wa_image.jpg", label: "WhatsApp — image search" },
];

const Orchestrix = () => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ position: "relative", px: pagePx, pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 8 }, overflow: "hidden" }}>
        <Box className="aurora" sx={{ width: 460, height: 460, background: "#06b6d4", top: -160, right: -80, opacity: 0.35 }} />
        <Box className="dot-grid" sx={{ position: "absolute", inset: 0, opacity: 0.4, maskImage: "radial-gradient(ellipse at 60% 0%, black, transparent 70%)" }} />
        <Box sx={{ maxWidth: 1080, mx: "auto", position: "relative", zIndex: 1 }}>
          <Button startIcon={<BsArrowLeft />} onClick={() => navigate("/")}
            sx={{ color: colors.inkSoft, textTransform: "none", mb: 4, "&:hover": { color: colors.accent } }}>
            Back to portfolio
          </Button>

          <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: "wrap", gap: 1 }}>
            <Chip label="Case Study" size="small" sx={{ background: colors.gradPrimary, color: "#0b1120", fontWeight: 700, fontFamily: font.mono, fontSize: 11 }} />
            <Chip label="AI Agents" size="small" sx={{ backgroundColor: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`, fontSize: 11 }} />
            <Chip label="Integrations" size="small" sx={{ backgroundColor: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`, fontSize: 11 }} />
          </Stack>

          <Typography variant="h1" sx={{ fontFamily: font.sans, fontWeight: 800, fontSize: { xs: 38, md: 60 }, color: colors.ink, mb: 2, lineHeight: 1.05 }}>
            Orchestrix
          </Typography>
          <Typography sx={{ fontSize: { xs: 17, md: 21 }, color: colors.inkSoft, maxWidth: 730, lineHeight: 1.6, mb: 4 }}>
            A SaaS platform that runs an entire e-commerce ordering journey <Strong>over WhatsApp</Strong>, driven by a
            stateful <Strong>LangGraph</Strong> AI agent — multimodal, with human handoff and deep <Strong>Shopify</Strong> sync.
          </Typography>

          <Grid container spacing={2} sx={{ maxWidth: 760, mb: 4 }}>
            {[
              ["Role", "Architect & engineer"],
              ["Scope", "AI engine · Backend · Integrations · Frontend"],
              ["Type", "Final-year capstone (PFE)"],
              ["Channel", "WhatsApp (Twilio)"],
            ].map(([k, v]) => (
              <Grid item xs={6} sm={3} key={k}>
                <Typography sx={{ fontFamily: font.mono, fontSize: 11.5, color: colors.inkMute, textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>{k}</Typography>
                <Typography sx={{ fontSize: 14, color: colors.ink, fontWeight: 500 }}>{v}</Typography>
              </Grid>
            ))}
          </Grid>

          <Button startIcon={<BsFilePdf />} href="/Rapport_PFE.pdf" target="_blank" rel="noopener noreferrer"
            sx={{ color: colors.ink, fontFamily: font.sans, fontWeight: 600, fontSize: 14.5, textTransform: "none", px: 2.5, py: 1.1, borderRadius: "12px", border: `1px solid ${colors.borderStrong}`, "&:hover": { borderColor: colors.accent, color: colors.accent } }}>
            Read the full report (PDF)
          </Button>
        </Box>
      </Box>

      {/* hero screenshot */}
      <Box sx={{ px: pagePx, mb: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1080, mx: "auto" }}>
          <Box component="img" src="/assets/orx_dashboard.jpg" alt="Orchestrix merchant dashboard"
            sx={{ width: "100%", borderRadius: "18px", border: `1px solid ${colors.borderStrong}`, boxShadow: "0 30px 80px rgba(0,0,0,0.55)", display: "block" }} />
        </Box>
      </Box>

      {/* Problem */}
      <Section>
        <Reveal>
          <Eyebrow>The Problem</Eyebrow>
          <H2>Small merchants live on WhatsApp — but selling there is all manual</H2>
          <P>
            In many markets, customers don't browse websites — they message the store on <Strong>WhatsApp</Strong>. For the
            merchant, that means answering every product question, building every cart, and taking every order by hand.
            It doesn't scale, it's slow, and it's expensive.
          </P>
          <P>
            Orchestrix automates the <Strong>entire ordering journey</Strong> on WhatsApp with an AI agent that greets the
            customer, understands intent, searches the catalogue, builds the cart, collects delivery details, confirms the
            order, and answers store questions — handing off to a human only when it needs to. It's a complete SaaS: the
            conversational channel plus a merchant dashboard and Shopify integration behind it.
          </P>
        </Reveal>
      </Section>

      {/* Capabilities */}
      <Section alt>
        <Reveal>
          <Eyebrow>What It Does</Eyebrow>
          <H2>A full commerce agent, not a scripted chatbot</H2>
        </Reveal>
        <Grid container spacing={2}>
          {capabilities.map((c, i) => (
            <Grid item xs={12} sm={6} md={4} key={c.name}>
              <Reveal delay={i * 60}>
                <Box sx={{ height: "100%", p: 2.6, borderRadius: "15px", backgroundColor: colors.surface, border: `1px solid ${colors.border}`, transition: "all 0.2s ease", "&:hover": { borderColor: "rgba(34,211,238,0.5)", transform: "translateY(-3px)" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2, color: colors.accent, "& svg": { fontSize: 22 } }}>
                    {c.icon}
                    <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15.5, color: colors.ink }}>{c.name}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13.5, color: colors.inkSoft, lineHeight: 1.65 }}>{c.text}</Typography>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* LangGraph engine */}
      <Section>
        <Reveal>
          <Eyebrow><Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}><TbBrandOpenai /> The AI Engine</Box></Eyebrow>
          <H2>LangGraph: a stateful, cyclic conversation graph</H2>
          <P>
            The heart of Orchestrix is a <Strong>LangGraph</Strong> state graph. Unlike a linear LangChain chain (a DAG),
            LangGraph models <Strong>cyclic</Strong> graphs — a node can return to a previous state. That's essential for
            commerce, where a customer changes their mind, backtracks, or asks a question at any point.
          </P>
          <P>
            The graph has <Strong>10 nodes</Strong> across a <Mono c={colors.cyan}>ConversationPhase</Mono> lifecycle
            (initialization → greeting → intent routing → order-taking → delivery → review → farewell). Each node exposes
            <Strong> tools</Strong> the LLM invokes via function calling — catalogue search, cart operations, pricing, order
            confirmation. State is persisted after every step with LangGraph's <Mono c={colors.green}>MemorySaver</Mono>
            checkpointer, keyed by the customer's WhatsApp number so conversations resume seamlessly across messages.
          </P>
        </Reveal>
        <Reveal>
          <DiagramCard caption="10-node LangGraph state graph · intent routing fans out into sub-flows · cyclic & checkpointed">
            <LangGraphDiagram />
          </DiagramCard>
        </Reveal>
        <Reveal>
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1.5, mt: 1 }}>
            {["Function calling", "GPT-4o-mini", "Per-node system prompts", "Guardrails injected", "LangSmith tracing"].map((t) => (
              <Box key={t} sx={{ fontSize: 13, color: colors.inkSoft, border: `1px solid ${colors.border}`, borderRadius: "9px", px: 1.4, py: 0.7, fontFamily: font.mono }}>{t}</Box>
            ))}
          </Stack>
        </Reveal>
      </Section>

      {/* WhatsApp */}
      <Section alt>
        <Reveal>
          <Eyebrow><Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}><BsWhatsapp /> WhatsApp Integration</Box></Eyebrow>
          <H2>Multimodal, multi-store message pipeline</H2>
          <P>
            WhatsApp is the primary channel, integrated through <Strong>Twilio</Strong>. Every inbound message hits a NestJS
            webhook, which resolves the right store's agent by Twilio number (<Strong>multi-tenant routing</Strong>), then
            runs it through the LangGraph engine. The agent handles <Strong>text, images, and voice</Strong>: product photos
            are matched by vision, voice notes are transcribed, and replies can be sent as text with product images or as a
            synthesized voice message.
          </P>
        </Reveal>
        <Reveal>
          <DiagramCard caption="Inbound WhatsApp → Twilio webhook → agent routing → LangGraph → multimodal response">
            <WhatsAppFlowDiagram />
          </DiagramCard>
        </Reveal>
        <Reveal>
          <P sx={{ mb: 0 }}>
            Production hardening included a per-user <Strong>daily rate limit</Strong> (protecting LLM and Twilio spend),
            and forcing <Strong>IPv4</Strong> for Twilio media fetches to work around IPv6 connectivity gaps in the target
            region — the kind of detail that only shows up when you actually ship.
          </P>
        </Reveal>
      </Section>

      {/* Human-in-the-loop */}
      <Section>
        <Reveal>
          <Eyebrow><Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}><TbHandStop /> Human-in-the-Loop</Box></Eyebrow>
          <H2>The merchant can step in — without breaking the AI's memory</H2>
          <P>
            Full automation covers the common case, but sensitive situations (a negotiation, a complaint) need a human. A
            merchant can <Strong>pause the agent</Strong> on a conversation from the dashboard and reply manually. Crucially,
            manual replies are stored in history <Strong>as agent messages</Strong> — so when the AI resumes, it has the
            complete context, including everything the merchant typed. The pause itself is also a classification signal: a
            conversation that required a handoff is scored accordingly.
          </P>
        </Reveal>
      </Section>

      {/* Shopify */}
      <Section alt>
        <Reveal>
          <Eyebrow><Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}><TbPlugConnected /> Shopify Integration</Box></Eyebrow>
          <H2>Bi-directional sync, kept live with webhooks</H2>
          <P>
            Merchants connect their Shopify store via <Strong>OAuth 2.0</Strong>. An initial sync <Strong>pulls</Strong>
            existing products, customers, and orders into Orchestrix, and <Strong>pushes</Strong> locally-created records
            back to Shopify. From then on, registered <Strong>webhooks</Strong> (
            <Mono>products/*</Mono>, <Mono>orders/*</Mono>, <Mono>customers/*</Mono>) keep both sides consistent in real time —
            so an order taken by the WhatsApp agent and one placed on Shopify converge into the same catalogue and dashboard.
          </P>
        </Reveal>
        <Reveal>
          <DiagramCard caption="OAuth connect · PULL/PUSH initial sync · real-time webhooks in both directions">
            <ShopifySyncDiagram />
          </DiagramCard>
        </Reveal>
      </Section>

      {/* Gallery */}
      <Section>
        <Reveal>
          <Eyebrow>Product</Eyebrow>
          <H2>The platform in action</H2>
        </Reveal>
        <Grid container spacing={2}>
          {shots.map((s, i) => {
            const phone = s.src.includes("wa_");
            return (
              <Grid item xs={12} sm={6} md={4} key={s.src}>
                <Reveal delay={i * 60}>
                  <Box sx={{ borderRadius: "14px", overflow: "hidden", border: `1px solid ${colors.border}`, backgroundColor: colors.surface }}>
                    <Box sx={{ height: 230, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: phone ? "#0d1424" : colors.surface, overflow: "hidden" }}>
                      <Box component="img" src={s.src} alt={s.label}
                        sx={{ width: phone ? "auto" : "100%", height: phone ? "100%" : "auto", maxHeight: phone ? "100%" : "none", objectFit: "cover", objectPosition: "top", display: "block" }} />
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: colors.inkSoft, fontFamily: font.mono, p: 1.4, borderTop: `1px solid ${colors.border}` }}>{s.label}</Typography>
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>
      </Section>

      {/* Tech stack + CTA */}
      <Section alt>
        <Reveal>
          <Eyebrow>Tech Stack</Eyebrow>
          <H2>What it's built with</H2>
        </Reveal>
        <Grid container spacing={2}>
          {stack.map((s, i) => (
            <Grid item xs={12} sm={6} md={3} key={s.group}>
              <Reveal delay={i * 50}>
                <Box sx={{ height: "100%", p: 2.2, borderRadius: "13px", backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <Typography sx={{ fontFamily: font.mono, fontSize: 11.5, color: colors.inkMute, textTransform: "uppercase", letterSpacing: 0.8, mb: 0.8 }}>{s.group}</Typography>
                  <Typography sx={{ fontSize: 14, color: colors.ink, lineHeight: 1.5 }}>{s.items}</Typography>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>

        <Reveal>
          <Box sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: "18px", border: "1px solid rgba(34,211,238,0.35)", background: "linear-gradient(135deg, rgba(34,211,238,0.1), rgba(99,102,241,0.05))", textAlign: "center" }}>
            <Typography sx={{ fontFamily: font.sans, fontWeight: 700, fontSize: { xs: 20, md: 24 }, color: colors.ink, mb: 1.5 }}>
              Want the deep technical detail?
            </Typography>
            <Typography sx={{ fontSize: 15, color: colors.inkSoft, mb: 3, maxWidth: 560, mx: "auto" }}>
              The full report covers the LangGraph node design, prompt engineering, and integration internals. Happy to walk through it.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button startIcon={<BsFilePdf />} href="/Rapport_PFE.pdf" target="_blank" rel="noopener noreferrer"
                sx={{ background: colors.gradPrimary, color: "#0b1120", fontWeight: 700, textTransform: "none", px: 3, py: 1.2, borderRadius: "12px", "&:hover": { transform: "translateY(-2px)" }, transition: "all 0.2s" }}>
                Read the full report
              </Button>
              <Button startIcon={<BsArrowLeft />} onClick={() => navigate("/")}
                sx={{ color: colors.ink, textTransform: "none", px: 3, py: 1.2, borderRadius: "12px", border: `1px solid ${colors.borderStrong}`, "&:hover": { borderColor: colors.accent } }}>
                Back to portfolio
              </Button>
            </Stack>
          </Box>
        </Reveal>
      </Section>
    </Box>
  );
};

export default Orchestrix;
